import { useGet, usePost } from "@albertine/api-client";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import { Lib } from "albertine-shared-web";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { convertPriceToString, currencyCodeAsSymbol } from "../../../common/currency";
import type { Booking } from "../../../common/types/Booking";
import type { Request } from "../../../common/types/Request";
import { Card } from "../../Common/Card/Card";
import Hr from "../../Common/Hr";
import { CategoryIconMap, PictogramIcon } from "../../Common/Icon";
import Input from "../../Common/Input";
import Label from "../../Common/Label";
import ModalOverlay from "../../Common/Overlay/ModalOverlay";
import Paragraph from "../../Common/Paragraph";

import "./RequestPaymentModal.css";
import { type Payment, PaymentMethodSchema } from "@albertine/types/openapi";
import { zodResolver } from "@hookform/resolvers/zod";
import { type ReactNode, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { isNumber } from "remeda";
import { z } from "zod";
import { toApiErrorMessage } from "../../../common/utils/apiError.util";
import { getTotalAndRemainingAmount } from "../../../common/utils/math.util";
import { EventEmitter } from "../../../EventEmitter";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import { PaymentCardList, type PaymentOption } from "./PaymentCardList";
import { PaymentItem } from "./PaymentItem";
import { TotalPaymentsRequested } from "./TotalPaymentsRequested";

type RequestPaymentModalProps = {
    onClose: (refetchPayments?: boolean) => void;
    booking: Booking;
    request: Request;
    member: MemberDataFirestoreRecord;
    previousPayments: Payment[];
    onPaymentChanged?: () => void;
};

export function RequestPaymentModal({
    onClose,
    booking,
    request,
    member,
    previousPayments,
    onPaymentChanged,
}: RequestPaymentModalProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const { data: stripeAccount } = useGet("memberStripeAccount", {
        param: { memberId: member.id },
    });

    const { data: paymentMethods, isLoading: fetchingPaymentMethods } = useGet(
        "paymentMethods",
        { param: { memberId: member.id } },
        { enabled: !!stripeAccount?.stripeCustomerId },
    );

    const { mutateAsync: createBookingPaymentMutation } =
        usePost("createBookingPayment");

    // Calculate the total amount already paid
    const { remainingAmount } = getTotalAndRemainingAmount(
        booking.price?.amount ?? 0,
        previousPayments.map((req) => req.amount ?? 0),
    );

    const defaultPriceString = remainingAmount.toFixed(2);
    const paymentToSupplier = booking.paymentTo === "supplier";

    const { data: paymentPrefs } = useGet("memberPaymentPreferences", {
        param: { memberId: member.id },
    });

    const memberFullName =
        Lib.Utils.Strings.combineWith(
            [member.firstName ?? undefined, member.lastName ?? undefined],
            " ",
        ) ?? "";

    const invoiceNameAliases = paymentPrefs?.invoiceNameAliases ?? [];
    const invoiceNameOptions = [
        ...(memberFullName ? [memberFullName] : []),
        ...invoiceNameAliases.filter(
            (alias) => alias.toLowerCase() !== memberFullName.toLowerCase(),
        ),
    ];

    const {
        control,
        setValue,
        handleSubmit,
        trigger,
        getValues,
        formState: { errors, isValid, defaultValues },
    } = useForm<RequestPaymentFormSchema>({
        defaultValues: {
            price: defaultPriceString,
            invoiceName: memberFullName,
            supplierAndCommission: "",
            otherDetails: paymentToSupplier
                ? "Member will pay directly at the property."
                : null,
            selectedOption: {},
        },
        mode: "onChange",
        resolver: zodResolver(PaymentFormSchema),
    });

    function handleOptionSelection(option: PaymentOption) {
        setValue("selectedOption", option);
        void trigger("selectedOption");
    }

    const invoiceName = useWatch({ control, name: "invoiceName" });

    async function handleSendRequestClick() {
        const valid = await trigger();
        if (valid) {
            setConfirmOpen(true);
        }
    }

    async function handleRequestPayment(data: RequestPaymentFormSchema) {
        if (!booking.priceCurrency) return;

        setIsLoading(true);

        try {
            await createBookingPaymentMutation({
                param: { bookingId: booking.id },
                json: {
                    amount: parseFloat(data.price),
                    currency: booking.priceCurrency,
                    supplierAndCommission: data.supplierAndCommission,
                    otherDetails: data.otherDetails,
                    paymentMethod: data.selectedOption.id,
                    invoiceName: data.invoiceName.trim(),
                },
            });

            setConfirmOpen(false);
            onClose(true);
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    message: "Request for payment sent",
                    type: "info",
                },
            });
        } catch (error) {
            console.error(error);
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    message: toApiErrorMessage(
                        error,
                        "Failed to send request for payment",
                    ),
                    type: "error",
                },
            });
        } finally {
            setIsLoading(false);
        }
    }

    if (!booking.priceCurrency) return null;

    return (
        <ModalOverlay.Base title="Request payment" onClose={onClose}>
            <ModalOverlay.Content padding="medium">
                <Lib.Flex.Column
                    className="request-payment-modal__card-container"
                    gap="value24"
                >
                    <Card className="request-payment-modal__card">
                        <Header
                            title={request.title ?? ""}
                            memberFullName={memberFullName}
                            booking={booking}
                        />

                        <Hr.XSmall />

                        <Lib.Flex.Column gap="value16">
                            <BookingDetails booking={booking} />

                            {previousPayments.length > 0 && <Hr.XSmall />}

                            {previousPayments.map((payment) => (
                                <PaymentItem
                                    inModal
                                    key={payment.createdAt}
                                    payment={payment}
                                    onChargeComplete={onPaymentChanged}
                                />
                            ))}
                        </Lib.Flex.Column>

                        <Hr.XSmall />

                        <TotalPaymentsRequested
                            booking={booking}
                            payments={previousPayments}
                        />

                        <Input.PersistentNumber
                            label="Payable to Albertine now"
                            prefix={currencyCodeAsSymbol(booking.priceCurrency)}
                            isValid={() => {
                                return !errors.price;
                            }}
                            value={defaultValues?.price ?? ""}
                            onChange={(value) => {
                                setValue("price", value);
                                void trigger("price");
                            }}
                        />
                    </Card>

                    <Lib.Flex.Column gap="value8">
                        <h4>Invoice name</h4>
                        <Input.TextField
                            label="Bill to"
                            placeholder="Name printed on the invoice"
                            isValid={() => {
                                return !errors.invoiceName;
                            }}
                            value={invoiceName}
                            onChange={(value) => {
                                setValue("invoiceName", value);
                                void trigger("invoiceName");
                            }}
                        />
                        {invoiceNameAliases.length > 0 && (
                            <Lib.Flex.Row gap="value8" style={{ flexWrap: "wrap" }}>
                                {invoiceNameOptions.map((name) => (
                                    <Button
                                        key={name}
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setValue("invoiceName", name);
                                            void trigger("invoiceName");
                                        }}
                                    >
                                        {name}
                                    </Button>
                                ))}
                            </Lib.Flex.Row>
                        )}
                    </Lib.Flex.Column>

                    <Lib.Flex.Column gap="value8">
                        <h4 id="card-selection-heading">Card to charge</h4>
                        {fetchingPaymentMethods ? (
                            <LoadingSpinner />
                        ) : (
                            <PaymentCardList
                                cards={paymentMethods ?? []}
                                onSelectionChange={handleOptionSelection}
                            />
                        )}
                    </Lib.Flex.Column>

                    <Lib.Flex.Column gap="value8">
                        <h4 id="card-selection-heading">Internal notes</h4>

                        <Input.TextField
                            label="Supplier and commission"
                            placeholder="Who did we book with and how much commission are we expecting?"
                            isValid={() => {
                                return !errors.supplierAndCommission;
                            }}
                            value={defaultValues?.supplierAndCommission ?? ""}
                            onChange={(value) => {
                                setValue("supplierAndCommission", value);
                                void trigger("supplierAndCommission");
                            }}
                        />

                        <Input.TextField
                            label="Other details"
                            placeholder="Any further notes about this payment?"
                            value={defaultValues?.otherDetails ?? ""}
                            onChange={(value) => {
                                setValue("otherDetails", value);
                                void trigger("otherDetails");
                            }}
                        />
                    </Lib.Flex.Column>
                </Lib.Flex.Column>
            </ModalOverlay.Content>

            <ModalOverlay.Footer>
                <Lib.Flex.Row gap="value12" justifyContent="end">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            onClose(false);
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        type="button"
                        disabled={!isValid || isLoading}
                        onClick={() => void handleSendRequestClick()}
                    >
                        {isLoading && <Spinner data-icon="inline-start" />}
                        Send request
                    </Button>
                </Lib.Flex.Row>
            </ModalOverlay.Footer>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent
                    className="z-[600] max-w-md"
                    overlayClassName="z-[600]"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Is this the correct currency and total amount?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            You are about to request a payment for{" "}
                            <strong>
                                {convertPriceToString({
                                    amount: parseFloat(getValues("price") || "0"),
                                    currency: booking.priceCurrency,
                                })}{" "}
                                ({booking.priceCurrency}).
                            </strong>
                            {invoiceName.trim() && (
                                <>
                                    <br />
                                    <br />
                                    Bill to: <strong>{invoiceName.trim()}</strong>
                                </>
                            )}
                            <br />
                            <br />
                            Please confirm before proceeding.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isLoading} type="button">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            type="button"
                            disabled={isLoading}
                            onClick={() => void handleSubmit(handleRequestPayment)()}
                        >
                            {isLoading ? "Sending…" : "Send request"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </ModalOverlay.Base>
    );
}

const PaymentOptionSchema = z.object({
    id: z.string(),
    label: z.string(),
    sublabel: z.string().or(z.custom<ReactNode>()).nullish(),
    type: PaymentMethodSchema,
});

const PaymentFormSchema = z.object({
    price: z
        .string()
        .min(1)
        .refine((value) => isPositivePaymentAmount(value)),
    invoiceName: z.string().trim().min(1),
    supplierAndCommission: z.string().min(1),
    otherDetails: z.string().nullable(),
    selectedOption: PaymentOptionSchema,
});

type RequestPaymentFormSchema = z.infer<typeof PaymentFormSchema>;

function isPositivePaymentAmount(price: string): boolean {
    const amount = Number.parseFloat(price);
    return isNumber(amount) && amount > 0;
}

function Header({
    title,
    memberFullName,
    booking,
}: {
    title: string;
    memberFullName: string;
    booking: Booking;
}) {
    const CategoryIconComponent =
        PictogramIcon[CategoryIconMap[booking.offeringCategory ?? "other"]];

    return (
        <Lib.Flex.Column
            gap="value4"
            alignItems="center"
            justifyContent="center"
            className="request-payment-modal__card-header"
        >
            <div className="request-payment-modal__card-icon">
                <CategoryIconComponent.Medium.P90 />
            </div>

            <h3>{title}</h3>
            <Paragraph.Medium.N50>{memberFullName}</Paragraph.Medium.N50>
        </Lib.Flex.Column>
    );
}

function BookingDetails({ booking }: { booking: Booking }) {
    if (!booking.priceCurrency) return null;

    return (
        <Lib.Flex.Row
            justifyContent="space-between"
            gap="value24"
            className="request-payment-modal__card-payment-details"
        >
            <div className="request-payment-modal__card-payment-details-title">
                <Label.Large.SemiBold.N10>{booking.title}</Label.Large.SemiBold.N10>
            </div>

            {
                <Label.Large.SemiBold.N10>
                    {convertPriceToString(
                        booking.price ?? {
                            amount: 0,
                            currency: booking.priceCurrency,
                        },
                    )}
                </Label.Large.SemiBold.N10>
            }
        </Lib.Flex.Row>
    );
}
